/**
 * API client for organization management.
 *
 * Backend endpoints:
 *   GET    /orgs/                   — list all orgs (ADM-006)
 *   POST   /orgs/                   — create org (ADM-006)
 *   PATCH  /orgs/{id}               — update org (ADM-006)
 *   PATCH  /orgs/{id}/capabilities  — update org capabilities (ADM-007)
 */
import { apiClient } from './client'

// ── Response types ───────────────────────────────────────────

export interface OrgResponse {
  id: string
  code: string
  name: string
  short_name: string | null
  kind: string
  is_active: boolean
  settings: Record<string, any>
  created_at: string
  updated_at: string
}

export interface OrgCapabilitiesResponse {
  org_id: string
  capabilities: Record<string, boolean>
}

// ── Request types ────────────────────────────────────────────

export interface OrgCreatePayload {
  code: string
  name: string
  short_name?: string | null
  kind?: string
}

export interface OrgUpdatePayload {
  name?: string
  short_name?: string | null
  kind?: string
  is_active?: boolean
}

export interface OrgCapabilitiesUpdatePayload {
  users_create?: boolean
  users_edit?: boolean
  users_deactivate?: boolean
  groups_manage?: boolean
  rls_manage?: boolean
  invites_create?: boolean
}

// ── API functions ────────────────────────────────────────────

export const orgsAPI = {
  /** List all organizations. */
  list: async (): Promise<OrgResponse[]> => {
    const response = await apiClient.get<OrgResponse[]>('/orgs/')
    return response.data
  },

  /** Create a new organization. */
  create: async (data: OrgCreatePayload): Promise<OrgResponse> => {
    const response = await apiClient.post<OrgResponse>('/orgs/', data)
    return response.data
  },

  /** Update organization details. */
  update: async (orgId: string, data: OrgUpdatePayload): Promise<OrgResponse> => {
    const response = await apiClient.patch<OrgResponse>(`/orgs/${orgId}`, data)
    return response.data
  },

  /** Deactivate organization (set is_active=false). */
  deactivate: async (orgId: string): Promise<OrgResponse> => {
    const response = await apiClient.post<OrgResponse>(`/orgs/${orgId}/deactivate`)
    return response.data
  },

  /** Update organization capabilities (feature toggles for org_admin). */
  updateCapabilities: async (
    orgId: string,
    data: OrgCapabilitiesUpdatePayload,
  ): Promise<OrgCapabilitiesResponse> => {
    const response = await apiClient.patch<OrgCapabilitiesResponse>(
      `/orgs/${orgId}/capabilities`,
      data,
    )
    return response.data
  },
}
